/**
 * ServiceConfigView — pinta el `config_json` que generan los ASISTENTES de servicio.
 *
 * Los asistentes (Diseñador de servicios → Asistentes) guardan TODO lo que preguntan
 * en `Service.config_json` con un espacio de nombres por asistente:
 *
 *   { "identity": { "platform": "Keycloak", "url": "https://id.empresa.local",
 *                   "providers": ["Google Workspace", "Microsoft Entra ID (M365)"], ... } }
 *
 * Hasta ahora esos datos no se pintaban en ninguna parte de la ficha del servicio
 * (solo la topología AD y la de VPN tenían su vista propia), así que el usuario
 * respondía 20 preguntas y no veía dónde iban a parar.
 *
 * Esta vista los agrupa por asistente, los traduce a etiquetas legibles y marca los
 * espacios de nombres que NO debe pintar porque ya tienen su propia pestaña.
 */
import './ServiceConfigView.css';

// Espacio de nombres → asistente. Se usa también para el título de cada bloque.
const NS_META = {
  identity:    { icon: '🔐', label: 'Identidad / SSO (IdP)' },
  backup:      { icon: '💾', label: 'Backup' },
  docker:      { icon: '🐳', label: 'Docker / contenedores' },
  virt:        { icon: '🧱', label: 'Virtualización' },
  vpn:         { icon: '🛡️', label: 'VPN' },
  wifi:        { icon: '📶', label: 'Red WiFi' },
  web:         { icon: '🌍', label: 'Aplicación web' },
  app:         { icon: '🧩', label: 'Servidor de aplicaciones' },
  db:          { icon: '🗄️', label: 'Base de datos' },
  dhcp:        { icon: '📡', label: 'DHCP' },
  dns:         { icon: '🌐', label: 'DNS' },
  mail:        { icon: '✉️', label: 'Correo' },
  proxy:       { icon: '⚖️', label: 'Proxy / balanceador' },
  monitoring:  { icon: '📈', label: 'Monitorización' },
  print:       { icon: '🖨️', label: 'Impresión' },
  files:       { icon: '📂', label: 'Servidor de ficheros' },
  rds:         { icon: '🖥️', label: 'RDS / granja de escritorio' },
  integration: { icon: '🔄', label: 'Integración / sincronización' },
};

// Espacios de nombres con pestaña propia (se pintan ahí, no aquí) o internos.
// `service_designer` es el layout del grafo (posiciones ocultas): no es un dato
// que haya respondido el usuario, así que no se enseña.
const HIDDEN_NS = new Set([
  'topology', 'ad_topology', 'layout', 'designer', 'service_designer', 'positions',
]);

// Etiquetas por clave, para cuando el mismo nombre significa cosas distintas.
const FULL_LABELS = {
  'identity.platform': 'Producto',
  'identity.deploy': 'Dónde vive',
  'app.platform': 'Runtime',
  'virt.platform': 'Plataforma',
  'docker.engine': 'Motor',
  'db.engine': 'Motor de base de datos',
  'mail.engine': 'Software de correo',
  'monitoring.engine': 'Plataforma de monitorización',
  'proxy.engine': 'Software',
  'print.spooler': 'Cola de impresión',
  'backup.engine': 'Software de backup',
  'identity.url': 'URL de acceso',
  'web.url': 'URL de acceso',
  'app.url': 'URL de acceso',
  'monitoring.url': 'URL de acceso',
  'proxy.public_url': 'URL pública',
  'docker.network': 'Red del host',
  'rds.subnet': 'Subred de la granja',
  'wifi.subnet': 'Subred WiFi',
};

// Etiquetas por clave (se reutilizan en cualquier asistente).
const KEY_LABELS = {
  name: 'Nombre', gateway: 'Puerta de enlace', psk: 'Clave (PSK)', subnet: 'Subred',
  url: 'URL', port: 'Puerto', protocol: 'Protocolo', version: 'Versión',
  tls: 'HTTPS / TLS', cert_cn: 'CN / SAN del certificado',
  mfa: 'Segundo factor (MFA)', providers: 'Proveedores externos',
  local_users: 'Cuentas locales', user_store: 'Dónde viven los usuarios',
  invites: 'Invitaciones por correo', self_register: 'Autorregistro',
  session: 'Duración de sesión', platform_group: 'Plataforma / suite',
  platform_role: 'Papel en la plataforma',
  engine: 'Motor / software', method: 'Método', frequency: 'Frecuencia',
  retention_days: 'Retención (días)', window: 'Ventana', encrypted: 'Cifrado en reposo',
  offsite: 'Copia externa (regla 3-2-1)', offsite_access: 'Acceso al destino remoto',
  offsite_creds: 'Credenciales / llaves', offsite_encrypt_transit: 'Cifrado en tránsito',
  registry: 'Registro de imágenes', orchestrated: 'Orquestado', exposed: 'Publica a Internet',
  ha: 'Alta disponibilidad', ha_config: 'Configuración de HA', ha_mode: 'Modo de HA',
  carp_vip: 'VIP compartida (CARP)', carp_vhid: 'VHID', sync_subnet: 'Subred de sincronización',
  xmlrpc_sync: 'Sincronización XML-RPC', kind: 'Tipo de VPN', client_pool: 'Pool de clientes',
  local_subnet: 'Subred local', remote_subnet: 'Subred remota', sites: 'Sedes',
  dns: 'DNS', dhcp: 'DHCP', dhcp_domain: 'Dominio DHCP', dhcp_pool: 'Rango DHCP',
  security: 'Seguridad', vlan: 'VLAN', guest: 'Red de invitados',
  guest_subnet: 'Subred de invitados', mgmt_subnet: 'Subred de gestión',
  fw_ha: 'Firewall en alta disponibilidad',
  storage_mode: 'Modo de almacenamiento', raid: 'RAID', hci_rf: 'Factor de réplica (HCI)',
  cluster_net: 'Red de clúster', mode: 'Modo', hosting: 'Alojamiento',
  domains: 'Dominios', relay: 'Relé', dns_auth: 'Autenticación DNS (SPF/DKIM/DMARC)',
  quota: 'Cuota', ad_publish: 'Publicación en AD', notify: 'Avisos',
  broker_ha: 'Broker en alta disponibilidad', external_access: 'Acceso externo',
  licensing: 'Licencias', profiles_mode: 'Perfiles', public_subnet: 'Subred pública',
  users: 'Usuarios', direction: 'Dirección', entities: 'Qué se sincroniza',
  conflict_policy: 'Resolución de conflictos', idempotent: 'Idempotencia',
  on_error: 'Si falla un registro', retries: 'Reintentos', endpoint: 'Endpoint / URL',
  auth: 'Autenticación', has_replica: 'Tiene réplica', replica_mode: 'Modo de réplica',
  authoritative: 'Autoritativo', reservations: 'Reservas', zones: 'Zonas',
  forwarders: 'Reenviadores', external: 'Acceso externo', labels: 'Etiquetas',
  targets: 'Elementos protegidos', repository: 'Repositorio', site: 'Sede',
  mfa_provider: 'Proveedor de MFA', sso_apps: 'Aplicaciones que se autentican',
};

const prettify = (key) => {
  const t = String(key || '').replace(/_/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

export const labelFor = (ns, key) =>
  FULL_LABELS[`${ns}.${key}`] || KEY_LABELS[key] || prettify(key);

const isEmpty = (v) =>
  v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

// La API devuelve el JSON con las claves ordenadas alfabeticamente, y el orden del
// asistente (que es el que tiene sentido: primero QUE es, luego donde, y por ultimo
// los detalles) se pierde. Estas claves se pintan primero, en este orden.
const FIRST_KEYS = [
  'platform', 'engine', 'deploy', 'url', 'public_url', 'endpoint', 'kind', 'method',
  'mode', 'storage_mode', 'targets', 'hosting', 'host', 'provider', 'providers',
];

/** Ordena las claves de un bloque: primero las relevantes, despues alfabeticamente. */
const orderEntries = (entries) =>
  [...entries].sort((a, b) => {
    const ia = FIRST_KEYS.indexOf(a[0]);
    const ib = FIRST_KEYS.indexOf(b[0]);
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    }
    return String(a[0]).localeCompare(String(b[0]));
  });

const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

const isUrl = (v) => typeof v === 'string' && /^https?:\/\//i.test(v.trim());

/** Bloques que se pueden pintar (fuera los que tienen su propia vista). */
export function visibleConfig(config) {
  if (!config || typeof config !== 'object') return [];
  return Object.entries(config).filter(
    ([ns, val]) => !HIDDEN_NS.has(ns) && isObject(val) && !isEmpty(val),
  );
}

function ValueView({ value, ns }) {
  if (isEmpty(value)) return null;
  if (typeof value === 'boolean') {
    return <span className={`scv-bool ${value ? 'is-yes' : 'is-no'}`}>{value ? 'Sí' : 'No'}</span>;
  }
  if (typeof value === 'number') return <span className="scv-text">{value}</span>;
  if (Array.isArray(value)) {
    // Lista de valores simples → chips.
    if (value.every((v) => !isObject(v))) {
      return (
        <span className="scv-chips">
          {value.filter((v) => !isEmpty(v)).map((v, i) => (
            <span className="scv-chip" key={i}>{String(v)}</span>
          ))}
        </span>
      );
    }
    // Lista de objetos (p. ej. las sedes de una VPN) → pares clave: valor.
    return (
      <div className="scv-sublist">
        {value.map((row, i) => (
          <div className="scv-subrow" key={i}>
            {Object.entries(row || {}).filter(([, v]) => !isEmpty(v)).map(([k, v]) => (
              <span className="scv-pair" key={k}>
                <em>{labelFor(ns, k)}:</em> {String(v)}
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (isObject(value)) return <Block data={value} ns={ns} nested />;
  const text = String(value);
  if (isUrl(text)) {
    return <a className="scv-link" href={text} target="_blank" rel="noreferrer">{text}</a>;
  }
  return <span className="scv-text">{text}</span>;
}

function Block({ data, ns, nested = false }) {
  const entries = orderEntries(Object.entries(data || {}).filter(([, v]) => !isEmpty(v)));
  if (!entries.length) return null;
  return (
    <div className={nested ? 'scv-rows is-nested' : 'scv-rows'}>
      {entries.map(([k, v]) => (
        isObject(v) ? (
          <div className="scv-subblock" key={k}>
            <div className="scv-subblock-title">{labelFor(ns, k)}</div>
            <Block data={v} ns={ns} nested />
          </div>
        ) : (
          <div className="scv-row" key={k}>
            <div className="scv-key">{labelFor(ns, k)}</div>
            <div className="scv-val"><ValueView value={v} ns={ns} /></div>
          </div>
        )
      ))}
    </div>
  );
}

export default function ServiceConfigView({ config, showHint = true, compact = false }) {
  const blocks = visibleConfig(config);
  if (!blocks.length) {
    return (
      <div className="scv-empty-state">
        Este servicio no tiene datos de asistente: o se creó a mano, o su asistente no
        guarda configuración (los datos aparecen aquí al crearlo con un asistente del
        Diseñador de servicios).
      </div>
    );
  }
  return (
    <div className={compact ? 'scv-root scv-compact' : 'scv-root'}>
      {showHint && (
        <p className="scv-hint">
          Esto es todo lo que respondiste en el asistente. Se guarda en el propio servicio
          (<code>config</code>) y las relaciones con los elementos del CMDB están en la
          pestaña <strong>Dependencias</strong> y en el Diseñador.
        </p>
      )}
      {blocks.map(([ns, val]) => {
        const meta = NS_META[ns] || {};
        return (
          <div className="scv-section" key={ns}>
            <div className="scv-section-title">
              <span className="scv-section-icon">{meta.icon || '🧾'}</span>
              {meta.label || prettify(ns)}
            </div>
            <Block data={val} ns={ns} />
          </div>
        );
      })}
    </div>
  );
}
